import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Edit, Save } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import styles from './TrainingManagementPage.module.scss';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useTrainings, useTrainingModules, useUpdateTrainingModule, useUpdateTraining } from '../../api/hooks';
import { TrainingDto, TrainingModuleDto } from '../../api/types/dtos/training';
import TrainingSelector from './components/TrainingSelector';
import ModuleAccordion from './components/ModuleAccordion';
import CreateModuleModal from './modals/CreateModuleModal';
import { moveUp, moveDown } from './utils/sequenceHelpers';
import { PRIVILEGES } from '../../api/types/dtos';
import { useHasPrivilege } from '../../components/authorization';

const TrainingManagementPage: React.FC = () => {
  const [selectedTraining, setSelectedTraining] = useState<TrainingDto | null>(null);
  const [editingTraining, setEditingTraining] = useState(false);
  const [trainingName, setTrainingName] = useState('');
  const [trainingDescription, setTrainingDescription] = useState('');
  const [createModuleModalOpen, setCreateModuleModalOpen] = useState(false);

  const { selectedOrganization, loading: organizationLoading } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPrivilege: canEditTraining } = useHasPrivilege(PRIVILEGES.UPDATE_TRAINING);

  // All hooks must be called before any conditional returns
  const { 
    data: trainings = [], 
    isLoading: trainingsLoading,
    error: trainingsError 
  } = useTrainings(
    selectedOrganization?.id || '',
    { isActive: true }
  );

  const { data: modules = [], isLoading: modulesLoading } = useTrainingModules(
    selectedOrganization?.id || '',
    selectedTraining?.id || '',
    { isActive: true }
  );

  const updateTraining = useUpdateTraining(selectedOrganization?.id || '');
  const updateModule = useUpdateTrainingModule(
    selectedOrganization?.id || '',
    selectedTraining?.id || ''
  );

  // Organization validation - after all hooks
  if (organizationLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!selectedOrganization?.id) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 3 }}>
          No se ha seleccionado una organización. Por favor, seleccione una organización para continuar.
        </Alert>
      </Container>
    );
  }

  const handleSelectTraining = (training: TrainingDto | null) => {
    setSelectedTraining(training);
    if (training) {
      setTrainingName(training.name);
      setTrainingDescription(training.description || '');
      setEditingTraining(false);
    }
  };

  const handleSaveTraining = async () => {
    if (!selectedTraining) return;

    try {
      await updateTraining.mutateAsync({
        id: selectedTraining.id,
        data: {
          name: trainingName.trim(),
          description: trainingDescription.trim() || undefined,
        },
      });
      enqueueSnackbar('Capacitación actualizada exitosamente', {
        variant: 'success',
      });
      setEditingTraining(false);
    } catch (error) {
      enqueueSnackbar('Error al actualizar la capacitación', { variant: 'error' });
      console.error('Error updating training:', error);
    }
  };

  const handleMoveModuleUp = async (module: TrainingModuleDto) => {
    const result = moveUp(module, modules);
    if (!result) return;

    try {
      await Promise.all([
        updateModule.mutateAsync({
          id: result.item.id,
          data: {
            name: result.item.name,
            description: result.item.description,
            skillName: result.item.skillName,
            sequence: result.item.sequence,
            progressContribution: result.item.progressContribution,
            trainingId: result.item.trainingId,
            logo: result.item.logo,
            isActive: result.item.isActive,
          },
        }),
        ...(result.swapItem
          ? [
              updateModule.mutateAsync({
                id: result.swapItem.id,
                data: {
                  name: result.swapItem.name,
                  description: result.swapItem.description,
                  skillName: result.swapItem.skillName,
                  sequence: result.swapItem.sequence,
                  progressContribution: result.swapItem.progressContribution,
                  trainingId: result.swapItem.trainingId,
                  logo: result.swapItem.logo,
                  isActive: result.swapItem.isActive,
                },
              }),
            ]
          : []),
      ]);
      enqueueSnackbar('Orden actualizado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error al actualizar el orden', { variant: 'error' });
    }
  };

  const handleMoveModuleDown = async (module: TrainingModuleDto) => {
    const result = moveDown(module, modules);
    if (!result) return;

    try {
      await Promise.all([
        updateModule.mutateAsync({
          id: result.item.id,
          data: {
            name: result.item.name,
            description: result.item.description,
            skillName: result.item.skillName,
            sequence: result.item.sequence,
            progressContribution: result.item.progressContribution,
            trainingId: result.item.trainingId,
            logo: result.item.logo,
            isActive: result.item.isActive,
          },
        }),
        ...(result.swapItem
          ? [
              updateModule.mutateAsync({
                id: result.swapItem.id,
                data: {
                  name: result.swapItem.name,
                  description: result.swapItem.description,
                  skillName: result.swapItem.skillName,
                  sequence: result.swapItem.sequence,
                  progressContribution: result.swapItem.progressContribution,
                  trainingId: result.swapItem.trainingId,
                  logo: result.swapItem.logo,
                  isActive: result.swapItem.isActive,
                },
              }),
            ]
          : []),
      ]);
      enqueueSnackbar('Orden actualizado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error al actualizar el orden', { variant: 'error' });
    }
  };

  const sortedModules = [...modules].sort((a, b) => a.sequence - b.sequence);

  return (
    <Container maxWidth="xl" className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h4" component="h1">
          Gestión de Capacitaciones
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Administre el contenido de sus capacitaciones: módulos, temas y contenidos
        </Typography>
      </Box>

      <Box className={styles.content}>
        <TrainingSelector
          trainings={trainings}
          selectedTraining={selectedTraining}
          onSelect={handleSelectTraining}
          loading={trainingsLoading}
        />

        {selectedTraining && (
          <>
            {/* Training Info Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Información de la Capacitación</Typography>
                {canEditTraining && !editingTraining && (
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => setEditingTraining(true)}
                  >
                    Editar
                  </Button>
                )}
                {canEditTraining && editingTraining && (
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSaveTraining}
                    disabled={updateTraining.isPending}
                  >
                    Guardar
                  </Button>
                )}
              </Box>

              {editingTraining ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Nombre"
                    value={trainingName}
                    onChange={(e) => setTrainingName(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Descripción"
                    value={trainingDescription}
                    onChange={(e) => setTrainingDescription(e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                  />
                </Box>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>
                    {selectedTraining.name}
                  </Typography>
                  {selectedTraining.description && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedTraining.description}
                    </Typography>
                  )}
                </>
              )}
            </Paper>

            {/* Modules Section */}
            <Paper sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Módulos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Organice los módulos, temas y contenidos de su capacitación
                </Typography>
              </Box>

              {modulesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : sortedModules.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No hay módulos creados. Agregue el primer módulo para comenzar.
                </Alert>
              ) : (
                sortedModules.map((module) => (
                  <ModuleAccordion
                    key={module.id}
                    module={module}
                    allModules={sortedModules}
                    trainingId={selectedTraining.id}
                    onMoveUp={handleMoveModuleUp}
                    onMoveDown={handleMoveModuleDown}
                  />
                ))
              )}

              {canEditTraining && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  fullWidth
                  size="large"
                  onClick={() => setCreateModuleModalOpen(true)}
                >
                  Agregar Módulo
                </Button>
              )}
            </Paper>

            <CreateModuleModal
              open={createModuleModalOpen}
              onClose={() => setCreateModuleModalOpen(false)}
              trainingId={selectedTraining.id}
              existingModules={modules}
            />
          </>
        )}

        {/* Error State */}
        {trainingsError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Error al cargar las capacitaciones. Por favor, intente refrescar la página.
          </Alert>
        )}

        {!selectedTraining && !trainingsError && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Seleccione una capacitación o cree una nueva para comenzar
            </Typography>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default TrainingManagementPage;

